import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../services/api';
import { showError } from '../../utils/notification';

// Модель заказа из БД (каждая позиция - отдельная запись)
interface OrderRecord {
    id: number;
    orderId: string;
    oneCGuid: string;
    status: string;
    productGuid: string;
    productCode: string;
    productName: string;
    quantity: number;
    unitPrice: number;
    uom: string;
    itemStatus: string;
    totalAmount: number;
    requestedDeliveryDate: string;
    confirmedDeliveryDate: string;
    deliveryAddress: string;
    warehouse: string;
    paymentTerms: string;
    moderatorName: string;
    notes: string;
    createdAt: string;
    updatedAt: string;
    sentToOneCAt: string;
    receivedFromOneCAt: string;
}

// Группированный заказ для отображения
// Статусы из 1С: ON_MODERATION, APPROVAL, SIGNED_BY_BOTH, READY_FOR_PICKING, SHIPPED, CLOSED
interface GroupedOrder {
    id: string;
    number: string;
    date: string;
    status: 'DRAFT' | 'PENDING' | 'SENT_TO_1C' | 'IN_1C_PROCESSING' | 'ON_MODERATION' | 'APPROVAL' | 'APPROVED' | 'SIGNED_BY_BOTH' | 'READY_FOR_PICKING' | 'SHIPPED' | 'CLOSED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED' | 'REJECTED';
    totalAmount: number;
    items: OrderItem[];
    oneCGuid?: string;
    moderatorName?: string;
    warehouse?: string;
    confirmedDeliveryDate?: string;
}

interface OrderItem {
    id: number;
    productName: string;
    productCode: string;
    quantity: number;
    unitPrice: number;
    totalPrice: number;
    uom: string;
    itemStatus?: string;
}

export const OrdersPage: React.FC = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<'all' | 'active' | 'completed'>('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [orders, setOrders] = useState<GroupedOrder[]>([]);
    const [loading, setLoading] = useState(true);

    // Загрузка заказов из БД
    useEffect(() => {
        loadOrders();
    }, []);

    const loadOrders = async () => {
        try {
            setLoading(true);
            const response = await api.orders.getMyOrders();

            if (response.success && response.data) {
                // Группируем записи по orderId
                const grouped = groupOrdersByOrderId(response.data);
                setOrders(grouped);
            } else {
                showError('Ошибка загрузки заказов', response.message || 'Не удалось загрузить список заказов');
            }
        } catch (error) {
            console.error('Error loading orders:', error);
            showError('Ошибка', 'Произошла ошибка при загрузке заказов');
        } finally {
            setLoading(false);
        }
    };

    // Группируем записи заказов по orderId
    const groupOrdersByOrderId = (records: OrderRecord[]): GroupedOrder[] => {
        const orderMap = new Map<string, GroupedOrder>();

        records.forEach(record => {
            const orderKey = record.orderId || `temp-${record.id}`;

            if (!orderMap.has(orderKey)) {
                // Создаем новую группу
                orderMap.set(orderKey, {
                    id: orderKey,
                    number: record.orderId || `PF-${String(record.id).padStart(6, '0')}`,
                    date: record.createdAt,
                    status: record.status as any,
                    totalAmount: 0,
                    items: [],
                    oneCGuid: record.oneCGuid,
                    moderatorName: record.moderatorName,
                    warehouse: record.warehouse,
                    confirmedDeliveryDate: record.confirmedDeliveryDate
                });
            }

            const order = orderMap.get(orderKey)!;

            // Добавляем позицию
            order.items.push({
                id: record.id,
                productName: record.productName,
                productCode: record.productCode,
                quantity: record.quantity,
                unitPrice: record.unitPrice || 0,
                totalPrice: record.totalAmount || 0,
                uom: record.uom,
                itemStatus: record.itemStatus
            });

            // Обновляем общую сумму
            order.totalAmount += (record.totalAmount || 0);
        });

        return Array.from(orderMap.values()).sort((a, b) =>
            new Date(b.date).getTime() - new Date(a.date).getTime()
        );
    };

    const getStatusColor = (status: GroupedOrder['status']) => {
        const statusColors: Record<string, string> = {
            // Начальные статусы
            DRAFT: 'bg-gray-100 text-gray-700',
            PENDING: 'bg-yellow-100 text-yellow-700',
            SENT_TO_1C: 'bg-blue-100 text-blue-700',
            IN_1C_PROCESSING: 'bg-blue-100 text-blue-700',
            // Статусы из 1С
            ON_MODERATION: 'bg-orange-100 text-orange-700',      // Ожидается согласование
            APPROVAL: 'bg-purple-100 text-purple-700',           // Готов к обеспечению
            APPROVED: 'bg-green-100 text-green-700',
            SIGNED_BY_BOTH: 'bg-emerald-100 text-emerald-700',   // Ожидается обеспечение
            READY_FOR_PICKING: 'bg-cyan-100 text-cyan-700',      // Готов к отгрузке
            SHIPPED: 'bg-indigo-100 text-indigo-700',            // В процессе отгрузки
            CLOSED: 'bg-teal-100 text-teal-700',                 // Закрыт
            // Прочие статусы
            IN_PROGRESS: 'bg-blue-100 text-blue-700',
            COMPLETED: 'bg-green-100 text-green-700',
            CANCELLED: 'bg-gray-100 text-gray-700',
            REJECTED: 'bg-red-100 text-red-700'
        };
        return statusColors[status] || 'bg-gray-100 text-gray-700';
    };

    const getStatusText = (status: GroupedOrder['status']) => {
        const statusTexts: Record<string, string> = {
            // Начальные статусы
            DRAFT: 'Черновик',
            PENDING: 'Отправлен в 1С',
            SENT_TO_1C: 'Отправлен в 1С',
            IN_1C_PROCESSING: 'Обрабатывается в 1С',
            // Статусы из 1С (русские названия)
            ON_MODERATION: 'Ожидается согласование',      // ОжидаетсяСогласование
            APPROVAL: 'Готов к обеспечению',              // ГотовКОбеспечению
            APPROVED: 'Согласовано',
            SIGNED_BY_BOTH: 'Ожидается обеспечение',      // ОжидаетсяОбеспечение
            READY_FOR_PICKING: 'Готов к отгрузке',        // ГотовКОтгрузке
            SHIPPED: 'В процессе отгрузки',               // ВПроцессеОтгрузки, ГотовКЗакрытию
            CLOSED: 'Закрыт',                             // Закрыт
            // Прочие статусы
            IN_PROGRESS: 'В работе',
            COMPLETED: 'Выполнен',
            CANCELLED: 'Отменён',
            REJECTED: 'Отклонён'
        };
        return statusTexts[status] || status;
    };

    // Активные статусы (заявка ещё в работе)
    const activeStatuses = ['DRAFT', 'PENDING', 'SENT_TO_1C', 'IN_1C_PROCESSING', 'ON_MODERATION', 'APPROVAL', 'APPROVED', 'SIGNED_BY_BOTH', 'READY_FOR_PICKING', 'IN_PROGRESS'];
    // Завершённые статусы
    const completedStatuses = ['SHIPPED', 'CLOSED', 'COMPLETED', 'CANCELLED', 'REJECTED'];

    const filteredOrders = orders.filter(order => {
        const matchesSearch = order.number.toLowerCase().includes(searchTerm.toLowerCase()) ||
            order.items.some(item => item.productName.toLowerCase().includes(searchTerm.toLowerCase()));

        if (activeTab === 'all') return matchesSearch;
        if (activeTab === 'active') return matchesSearch && activeStatuses.includes(order.status);
        if (activeTab === 'completed') return matchesSearch && completedStatuses.includes(order.status);

        return matchesSearch;
    });

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('ru-RU', {
            style: 'currency',
            currency: 'KZT',
            minimumFractionDigits: 0
        }).format(amount);
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    // Loading состояние
    if (loading) {
        return (
            <div className="space-y-6">
                <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Заявки-покупки</h1>
                    <p className="text-gray-600 mt-1">Управляйте своими заявками на покупку продукции</p>
                </div>
                <button
                    onClick={() => navigate('/client/orders/create')}
                    className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3 rounded-xl font-medium hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200 flex items-center"
                >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Создать заявку
                </button>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {[
                    { label: 'Всего заявок', value: orders.length, color: 'blue' },
                    { label: 'Активные', value: orders.filter(o => activeStatuses.includes(o.status)).length, color: 'purple' },
                    { label: 'Завершено', value: orders.filter(o => completedStatuses.includes(o.status)).length, color: 'green' },
                    { label: 'Общая сумма', value: formatCurrency(orders.reduce((sum, o) => sum + o.totalAmount, 0)), color: 'indigo' }
                ].map((stat, index) => (
                    <div key={index} className={`bg-gradient-to-r ${stat.color === 'blue' ? 'from-blue-50 to-blue-100 border-blue-200' :
                        stat.color === 'purple' ? 'from-purple-50 to-purple-100 border-purple-200' :
                            stat.color === 'green' ? 'from-green-50 to-green-100 border-green-200' :
                                'from-indigo-50 to-indigo-100 border-indigo-200'
                        } border rounded-xl p-6`}>
                        <h3 className="text-sm font-medium text-gray-600">{stat.label}</h3>
                        <p className={`text-2xl font-bold mt-2 ${stat.color === 'blue' ? 'text-blue-600' :
                            stat.color === 'purple' ? 'text-purple-600' :
                                stat.color === 'green' ? 'text-green-600' :
                                    'text-indigo-600'
                            }`}>{stat.value}</p>
                    </div>
                ))}
            </div>

            {/* Filters and Search */}
            <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    {/* Tabs */}
                    <div className="flex space-x-1 bg-gray-100 rounded-lg p-1">
                        {[
                            { key: 'all', label: 'Все заявки' },
                            { key: 'active', label: 'Активные' },
                            { key: 'completed', label: 'Завершенные' }
                        ].map((tab) => (
                            <button
                                key={tab.key}
                                onClick={() => setActiveTab(tab.key as any)}
                                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${activeTab === tab.key
                                    ? 'bg-white text-gray-900 shadow-sm'
                                    : 'text-gray-600 hover:text-gray-900'
                                    }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    {/* Search */}
                    <div className="relative">
                        <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <input
                            type="text"
                            placeholder="Поиск по номеру заявки или продукту..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full sm:w-80"
                        />
                    </div>
                </div>
            </div>

            {/* Orders List */}
            <div className="space-y-4">
                {filteredOrders.length === 0 ? (
                    <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                            </svg>
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">Заявки не найдены</h3>
                        <p className="text-gray-600 mb-6">Попробуйте изменить параметры поиска или создайте новую заявку</p>
                        <button
                            onClick={() => navigate('/client/orders/create')}
                            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            Создать первую заявку
                        </button>
                    </div>
                ) : (
                    filteredOrders.map((order) => (
                        <div key={order.id} className="bg-white rounded-2xl border border-gray-200 hover:shadow-lg transition-all duration-200 overflow-hidden">
                            <div className="p-6">
                                {/* Order Header */}
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                                    <div className="flex items-center space-x-4">
                                        <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl flex items-center justify-center">
                                            <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                                            </svg>
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-semibold text-gray-900">{order.number}</h3>
                                            <p className="text-sm text-gray-600">от {formatDate(order.date)}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center space-x-4">
                                        <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(order.status)}`}>
                                            {getStatusText(order.status)}
                                        </span>
                                        <span className="text-lg font-bold text-gray-900">
                                            {formatCurrency(order.totalAmount)}
                                        </span>
                                    </div>
                                </div>

                                {/* Order Items */}
                                <div className="bg-gray-50 rounded-xl p-4 mb-4">
                                    <h4 className="text-sm font-medium text-gray-900 mb-3">Позиции заказа:</h4>
                                    <div className="space-y-2">
                                        {order.items.map((item) => (
                                            <div key={item.id} className="flex items-center justify-between text-sm">
                                                <span className="text-gray-700">{item.productName}</span>
                                                <div className="flex items-center space-x-4 text-gray-600">
                                                    <span>{item.quantity} {item.uom || 'шт'}.</span>
                                                    <span>×</span>
                                                    <span>{formatCurrency(item.unitPrice)}</span>
                                                    <span>=</span>
                                                    <span className="font-medium text-gray-900">{formatCurrency(item.totalPrice)}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Actions */}
                                <div className="flex flex-col sm:flex-row gap-3">
                                    {/* Кнопка подписания показывается для статуса APPROVAL (ГотовКОбеспечению) */}
                                    {(order.status === 'APPROVAL') && (
                                        <button
                                            onClick={async () => {
                                                try {
                                                    // Получаем заявку по orderId
                                                    const { orderSignatureService } = await import('../../services/orderSignatureService');
                                                    const signature = await orderSignatureService.getOrderSignatureByOrderId(order.number);
                                                    navigate(`/client/order-signatures/${signature.id}`);
                                                } catch (error) {
                                                    console.error('Ошибка получения заявки:', error);
                                                }
                                            }}
                                            className="flex-1 bg-gradient-to-r from-green-600 to-green-700 text-white px-4 py-2 rounded-lg hover:shadow-lg transition-all font-medium flex items-center justify-center"
                                        >
                                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                            Подписать заявку
                                        </button>
                                    )}
                                    <button
                                        onClick={() => navigate(`/client/orders/${order.id}`)}
                                        className="flex-1 bg-blue-50 text-blue-700 px-4 py-2 rounded-lg hover:bg-blue-100 transition-colors font-medium flex items-center justify-center"
                                    >
                                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                        </svg>
                                        Просмотреть
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};
